import type { Uuid } from "spacetimedb";
import { ImageOffIcon } from "lucide-react";
import { Suspense, useMemo, useState } from "react";

import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { Skeleton } from "@/web/components/primitives/skeleton";
import { ErrorHandler } from "@/web/lib/errors";
import { usePlacePhotoUrlByPlaceId } from "@/web/lib/googlePlaces/photoUtils";
import { cn } from "@/web/lib/styling";
import { useReducerMutation } from "@/web/lib/tanstack";
import { reducers } from "@tycho/web-bindings";
import { useSelectedBrand } from "@/web/routes/_protected/smb/-dashboard/brandSelection";
import {
  useSuspendedOperatingProperties,
  useSuspendedOperatingPropertiesJobStatus,
} from "@/web/routes/_protected/smb/-dashboard/dashboardData";
import type { OperatingPropertyForUserRow } from "@tycho/web-bindings/types";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

function ConfirmOperatingLocationsAction({ onSkip, onComplete }: SmbActionCoreProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ActionCard category="negotiate">
        <ActionCard.Header>
          <ActionCard.Badge>Price</ActionCard.Badge>
          <ActionCard.DetailsTrigger />
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>Confirm where you operate</ActionCard.Title>
          <ActionCard.Description>
            We found the locations your business appears to operate from. Confirm the ones that are
            really yours so we can scope financing to the right properties.
          </ActionCard.Description>
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <ActionCard.PrimaryButton onClick={() => setOpen(true)}>
            Review locations
          </ActionCard.PrimaryButton>
        </ActionCard.Footer>
        <ActionCard.Back title="Why we ask">
          <p>
            Knowing every place you operate lets us line up the right collateral and tailor offers
            to your actual footprint — not just the address you signed up with.
          </p>
        </ActionCard.Back>
      </ActionCard>

      <ConfirmOperatingLocationsDrawer open={open} onOpenChange={setOpen} onComplete={onComplete} />
    </>
  );
}

/** A selected location: keyed by string id, carries the bigint id + brandId. */
type Selection = { id: Uuid; brandId: Uuid };

function ConfirmOperatingLocationsDrawer({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (result?: unknown) => void;
}) {
  // Selection map keyed by id string (bigints aren't great Set/Map keys to
  // compare). Values carry the raw bigint id + brandId so the footer can build
  // the reducer payload without re-querying.
  const [selected, setSelected] = useState<ReadonlyMap<string, Selection>>(new Map());

  const { mutateAsync: confirmLocations, isPending } = useReducerMutation(
    reducers.confirmOperatingLocations,
  );

  const ids = [...selected.values()].map((s) => s.id);
  const brandId = [...selected.values()][0]?.brandId ?? null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Confirm operating locations</DrawerTitle>
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody className="flex flex-col gap-4">
          {/*
            Local Suspense boundary keeps the spinner inside the drawer instead
            of bubbling to the app root, matching SelectPropertyAction.
          */}
          <Suspense fallback={<OperatingLocationsSkeleton />}>
            <OperatingLocationsList selected={selected} onChange={setSelected} />
          </Suspense>
        </DrawerBody>
        <DrawerFooter>
          <Button
            color="red"
            size="lg"
            disabled={selected.size === 0 || isPending}
            isLoading={isPending}
            onClick={async () => {
              if (brandId === null || ids.length === 0) return;
              try {
                await confirmLocations({ brand: brandId, operatingPropertyIds: ids });
                onOpenChange(false);
                onComplete({ confirmedOperatingPropertyIds: ids });
              } catch (error) {
                ErrorHandler.default(error);
              }
            }}
          >
            Confirm
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function OperatingLocationsList({
  selected,
  onChange,
}: {
  selected: ReadonlyMap<string, Selection>;
  onChange: (selected: ReadonlyMap<string, Selection>) => void;
}) {
  const operatingProperties = useSuspendedOperatingProperties();
  // Brand-scoped job status lives in its own view so it has a carrier even when
  // the job finished with zero results — `allOperatingPropertiesForUser` would
  // be empty in that case and couldn't tell us "loading" from "done, empty".
  const jobStatuses = useSuspendedOperatingPropertiesJobStatus();

  // Scoped to the brand chosen in the dashboard's brand switcher (the sole brand
  // for single-brand users). See `brandSelection.tsx`.
  const selectedBrand = useSelectedBrand();
  const selectedBrandId = selectedBrand?.id ?? null;

  const propertiesForBrand = useMemo(() => {
    if (selectedBrandId === null) return [];
    return operatingProperties.filter((p) => p.brand.compareTo(selectedBrandId) === 0);
  }, [operatingProperties, selectedBrandId]);

  const jobStatus =
    selectedBrandId === null
      ? undefined
      : jobStatuses.find((s) => s.brand.compareTo(selectedBrandId) === 0)?.jobStatus?.tag;

  // Discovery is async and best-effort, but the brand's own onboarding address
  // is seeded as an operating location up front (see `submitUserInfo`). So the
  // list is never empty for a normally-onboarded brand: there's always at least
  // that "default" location to confirm, even when the async discovery job finds
  // nothing or fails outright. That's what lets us drop the old "nothing to
  // confirm, skip" failure state — which previously soft-locked the user — and
  // fold any failure into a muted footnote under the list instead.
  const isSearching = jobStatus === "Pending";

  // Only spin when we genuinely have nothing to show yet AND a job is still in
  // flight — i.e. a legacy brand whose default wasn't seeded. A normally
  // onboarded brand skips this because its default row replicates with the
  // brand itself.
  if (propertiesForBrand.length === 0 && isSearching) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">Looking up where your business operates…</p>
        <OperatingLocationsSkeleton />
      </div>
    );
  }

  // Muted footnote under the list. While discovery runs we say so; once it has
  // settled with no locations beyond the default, we tell the user we didn't
  // find any others (this folds the old failure / no-results state into the
  // list). When extras were discovered we say nothing.
  const discoveryNote = isSearching
    ? "Looking for additional locations…"
    : propertiesForBrand.length <= 1
      ? "We didn't find any other operating locations."
      : null;

  const toggle = (p: OperatingPropertyForUserRow) => {
    const key = p.id.toString();
    const next = new Map(selected);
    if (next.has(key)) next.delete(key);
    else next.set(key, { id: p.id, brandId: p.brand });
    onChange(next);
  };

  return (
    <div className="flex flex-col">
      <ul className="flex flex-col gap-4 px-2 pt-6">
        {propertiesForBrand.map((p) => {
          const key = p.id.toString();
          return (
            <OperatingLocationCard
              key={key}
              property={p}
              selected={selected.has(key)}
              onToggle={() => toggle(p)}
            />
          );
        })}
      </ul>
      {discoveryNote ? (
        // Stable hook for e2e (copy-independent): the discovery job has settled
        // and surfaced no locations beyond the seeded default.
        <p
          data-testid="operating-locations-discovery-note"
          className="px-2 pt-4 pb-6 text-sm text-muted-foreground"
        >
          {discoveryNote}
        </p>
      ) : null}
    </div>
  );
}

function OperatingLocationCard({
  property,
  selected,
  onToggle,
}: {
  property: OperatingPropertyForUserRow;
  selected: boolean;
  onToggle: () => void;
}) {
  // Resolve the hero photo from the Google Place ID. Athena only ever returns
  // the place id (never a photo resource name), so we fetch place details
  // client-side and take the first photo. Bytes go browser -> Google directly.
  const { url: photoUrl, isPending: photoPending } = usePlacePhotoUrlByPlaceId(
    property.googlePlaceId,
    480,
  );
  const locationLine = formatLocationLine(property);

  return (
    <li className="relative">
      {selected ? (
        <div
          className="pointer-events-none absolute -inset-2 z-20 outline-2 outline-withus-red"
          aria-hidden
        />
      ) : null}
      {selected ? (
        <div className="pointer-events-none absolute -top-2 left-2 z-30 bg-withus-red px-2 py-1.5 text-xs font-semibold tracking-wide text-withus-white uppercase">
          Selected
        </div>
      ) : null}
      <button
        type="button"
        aria-pressed={selected}
        onClick={onToggle}
        className="relative flex h-64 w-full cursor-pointer overflow-hidden bg-muted text-left outline-none focus-visible:ring-2 focus-visible:ring-withus-red focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {photoUrl ? (
          <>
            <img src={photoUrl} alt="" className="absolute inset-0 size-full object-cover" />
            <div className="absolute inset-0 bg-withus-pure-black/40" aria-hidden />
          </>
        ) : photoPending ? (
          <div className="absolute inset-0 bg-muted" aria-hidden />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground"
            aria-hidden
          >
            <ImageOffIcon className="size-12" />
          </div>
        )}
        <div className="relative z-10 flex h-full w-full flex-col justify-between p-5">
          <div
            className={cn(
              "font-heading text-xl leading-6 uppercase",
              photoUrl ? "text-withus-white" : "text-foreground",
            )}
          >
            <p>{property.canonicalAddress.addressLine1}</p>
            {locationLine ? <p>{locationLine}</p> : null}
          </div>
        </div>
      </button>
    </li>
  );
}

function formatLocationLine(property: OperatingPropertyForUserRow): string {
  const cityState = [property.canonicalAddress.city, property.canonicalAddress.state]
    .filter(Boolean)
    .join(", ");
  return cityState || property.canonicalAddress.zip || property.canonicalAddress.countryCode || "";
}

function OperatingLocationsSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

export function createConfirmOperatingLocationsAction(
  props: { id?: string; canSkip?: boolean } = {},
): SmbActionDefinition {
  const { id = "confirm-operating-locations", canSkip = true } = props;
  return {
    id,
    category: "negotiate",
    canSkip,
    render: (core) => <ConfirmOperatingLocationsAction {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";

// Priority above select-property (100) so confirming operating locations comes
// first. Once the user confirms ≥1 location it disappears (see predicate) and
// the select-target card — now backed by the populated catalog — takes over.
export const confirmOperatingLocationsRule = CardRulesEngine.singletonRule({
  id: "confirm-operating-locations",
  priority: 110,
  when: CardRulesEngine.hasNoConfirmedOperatingLocation,
  build: () => createConfirmOperatingLocationsAction(),
});
